import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { SDLC_PATH } from '../../config';
import { countLabel, phaseReference } from '../../content';
import {
  cardSurfaceSx,
  flushStageEdgeCardSx,
  interactiveCardSx,
  pressedCardSx,
  stageColourFor,
  tokens,
} from '../../theme';
import type { PhaseCardCrossCuttingProps } from './types';

/**
 * Delivery Leadership: the phase that is not a step in the flow.
 *
 * **Full width, below the grid rather than beside it.** It runs alongside every
 * stage rather than after one, so a fifth column would say the opposite — that it
 * happens once, at the end.
 *
 * The pink edge is the Delivery stage colour and **deliberately not the brand
 * red**, which is reserved for `action.primary`: an edge in the action colour
 * reads as something to click.
 *
 * **Its type is the phase cards' type**, not the step larger the designs set —
 * which read as a different kind of object rather than the same card laid out
 * wide.
 */

/**
 * The line under the description. **Hardcoded, because half of it has no source**
 * — the design's own words about what cross-cutting means, not a field.
 */
const CROSS_CUTTING_SUFFIX = 'runs throughout delivery';

export const PhaseCardCrossCutting = ({ phase }: PhaseCardCrossCuttingProps) => {
  const stageColour = stageColourFor(phase.stage, 'onSurface');

  return (
    <RouterLink to={`${SDLC_PATH}/${phase.path}`} style={{ textDecoration: 'none' }}>
      <Box
        sx={{
          ...cardSurfaceSx,
          ...interactiveCardSx,
          ...flushStageEdgeCardSx,
          ...pressedCardSx(stageColour),
          overflow: 'hidden',
        }}
      >
        <Box sx={{ height: 4, backgroundColor: stageColour }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            paddingBlock: 8,
            paddingInline: { xs: 4, sm: 8, md: 12 },
          }}
        >
          <Typography
            variant="caption"
            component="span"
            sx={{ fontWeight: 500, letterSpacing: '1px', color: 'text.secondary' }}
          >
            {phaseReference(phase.number)}
          </Typography>

          {/* The same `h3` the phase cards use, at the same weight. The designs
              set this one larger and bolder, which read as a different kind of
              thing rather than as the same card laid out wide. */}
          <Typography variant="h3" component="h2" sx={{ color: 'text.primary' }}>
            {phase.name}
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 860 }}
          >
            {phase.description}
          </Typography>

          {phase.methodCount !== undefined && (
            <Typography
              variant="body2"
              component="p"
              sx={{ fontFamily: tokens.fontFamily.heading, fontWeight: 500, color: stageColour }}
            >
              {/* The `·` is the component's, like every other glyph in the
                  designs — the API returns a number and a name. */}
              {countLabel(phase.methodCount, 'method')} &middot; {CROSS_CUTTING_SUFFIX}
            </Typography>
          )}
        </Box>
      </Box>
    </RouterLink>
  );
};
